import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@weco/ui";
import { useAssistantConversation } from "../../../modules/assistant/state/AssistantConversationProvider";
import { ComposerBar } from "../../../modules/assistant/components/ComposerBar";
import { useChatConfirmationHandlers } from "../../../modules/assistant/hooks/useChatConfirmationHandlers";
import { useAssistantConfig } from "../useAssistantConfig";
import { Bubble, FormattedText } from "./FormattedText";
import { ProposalCard, type ProposalOutcome } from "./ProposalCard";
import type { ChatMessage, ConfirmationRequest } from "../../../modules/assistant/types/chat";

/** Kit Appendix E starter chips — shown only on an empty, idle thread. */
const STARTERS = [
  "What's on for me today?",
  "Help me plan my morning",
  "What should I focus on first?",
  "Anything I need to catch up on?",
] as const;

/** Maps a tool-call name to the kit's friendly "what Mochi is doing" label. */
const ACTIVITY_LABELS: Record<string, string> = {
  get_upcoming_events: "Looking at your day",
  get_daily_briefing: "Looking at your day",
  get_inbox_summary: "Checking your inbox",
  search_emails: "Checking your inbox",
  search_files: "Looking through your files",
  list_recent_files: "Looking through your files",
  get_office_report: "Checking your workspace",
};

/** Best-effort "what is Mochi doing right now" label for a message that is
 * still streaming with no visible text yet. Reads the latest tool-call name
 * recorded on the in-flight assistant message; falls back to "Thinking" when
 * there is none (e.g. the model hasn't invoked a tool before its first
 * token). */
export function activityLabelFor(message: ChatMessage | undefined): string | null {
  if (!message || !message.isStreaming) return null;
  const lastTool = message.toolCalls?.at(-1);
  if (!lastTool) return null;
  return ACTIVITY_LABELS[lastTool.name] ?? "Thinking";
}

export function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" role="status" aria-label="Mochi is typing">
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.3s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.15s]" />
      <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
    </span>
  );
}

function BlinkingCaret() {
  return <span className="ml-0.5 inline-block h-4 w-[2px] animate-pulse bg-slate-500 align-middle" aria-hidden />;
}

/**
 * One rendered message, including the kit `ProposalCard` for a pending (or
 * already-resolved) action confirmation carried on `message.confirmation`.
 *
 * Outcome is tracked locally: PENDING while `message.confirmation` is set and
 * not yet acted on; CONFIRMED/DISMISSED once the user clicks Confirm/Not now.
 * The confirmation payload is snapshotted into local state on first sight so
 * the card keeps rendering (as Done/Dismissed) even after the server clears
 * `message.confirmation` post-resolution.
 */
export function MessageItem({ message }: { message: ChatMessage }) {
  const chat = useAssistantConversation();
  const { onConfirm, onReject } = useChatConfirmationHandlers(chat);
  const [confirmationSnapshot, setConfirmationSnapshot] = useState<ConfirmationRequest | null>(
    message.confirmation ?? null,
  );
  const [outcome, setOutcome] = useState<ProposalOutcome>("PENDING");

  useEffect(() => {
    if (message.confirmation) setConfirmationSnapshot(message.confirmation);
  }, [message.confirmation]);

  const handleConfirm = () => {
    onConfirm();
    setOutcome("CONFIRMED");
  };
  const handleReject = () => {
    onReject();
    setOutcome("DISMISSED");
  };

  const showCaret = message.role === "assistant" && message.isStreaming && message.content.length > 0;
  return (
    <div className="space-y-1">
      <Bubble role={message.role}>
        <FormattedText text={message.content} />
        {showCaret && <BlinkingCaret />}
      </Bubble>
      {confirmationSnapshot && (
        <ProposalCard
          confirmation={confirmationSnapshot}
          outcome={outcome}
          onConfirm={handleConfirm}
          onReject={handleReject}
        />
      )}
    </div>
  );
}

export function Conversation() {
  const { messages, status, sendMessage, errorMessage } = useAssistantConversation();
  const { assistant_name, ai_available } = useAssistantConfig();
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const announcedIdRef = useRef<string | null>(null);

  const lastMessage = messages.at(-1);
  const activity = useMemo(
    () => (lastMessage?.role === "assistant" ? activityLabelFor(lastMessage) : null),
    [lastMessage],
  );
  const waitingForText =
    status === "streaming" && lastMessage?.role === "assistant" && lastMessage.isStreaming && !lastMessage.content;

  // aria-live announcement fires once per finished assistant reply, not per
  // streamed token (mirrors the kit's once-per-reply behaviour).
  const finishedAnnouncement = useMemo(() => {
    const last = messages.at(-1);
    if (!last || last.role !== "assistant" || last.isStreaming) return null;
    if (announcedIdRef.current === last.id) return null;
    return last;
  }, [messages]);

  useEffect(() => {
    if (finishedAnnouncement) announcedIdRef.current = finishedAnnouncement.id;
  }, [finishedAnnouncement]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [messages, lastMessage?.content]);

  const isEmpty = messages.length === 0;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3">
        <div className="space-y-3">
          {!ai_available && (
            <div
              className={cn(
                "rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800",
                "ring-1 ring-inset ring-amber-200",
              )}
            >
              I can't talk just yet — an admin needs to connect me to the AI service by setting{" "}
              <code className="rounded bg-amber-100 px-1 py-0.5 font-mono text-[11px]">
                ANTHROPIC_API_KEY
              </code>{" "}
              on the server. Everything else works as normal.
            </div>
          )}

          {errorMessage && (
            <div
              role="alert"
              className={cn(
                "rounded-xl bg-rose-50 px-3 py-2 text-xs text-rose-800",
                "ring-1 ring-inset ring-rose-200",
              )}
            >
              {errorMessage}
            </div>
          )}

          {isEmpty ? (
            <>
              <Bubble role="assistant">
                <FormattedText text={`Hello! I'm ${assistant_name}. How can I help you today?`} />
              </Bubble>
              <div className="flex flex-wrap gap-1.5">
                {STARTERS.map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => sendMessage(starter)}
                    className={cn(
                      "rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 text-xs font-medium text-slate-700",
                      "hover:bg-white transition-colors",
                    )}
                  >
                    {starter}
                  </button>
                ))}
              </div>
            </>
          ) : (
            messages.map((message) => <MessageItem key={message.id} message={message} />)
          )}

          {waitingForText && (
            <div className="flex items-center gap-2 px-1 text-xs text-slate-500">
              <TypingDots />
              <span>{activity ? `${activity}…` : "Thinking…"}</span>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      </div>

      <div aria-live="polite" className="sr-only">
        {finishedAnnouncement ? markdownToSpeech(finishedAnnouncement.content) : ""}
      </div>

      <div className="shrink-0 border-t border-slate-200/70 px-3 py-2">
        <ComposerBar
          onSubmit={(text, attachmentId) => void sendMessage(text, undefined, attachmentId)}
          disabled={!ai_available || status === "streaming" || status === "awaiting_confirmation"}
        />
      </div>
    </div>
  );
}

/**
 * The reply as a screen reader should hear it: the visible bubble renders the
 * markdown, but this live region is plain text, so without this it read the
 * raw `**`, `#` and backticks aloud (and select-all copied them).
 */
export function markdownToSpeech(md: string): string {
  return md
    .replace(/```[\s\S]*?```/g, (block) => block.replace(/```\w*\n?/g, ""))
    .replace(/`([^`]+)`/g, "$1")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*|__)(.+?)\1/g, "$2")
    .replace(/(^|[^*\w])[*_]([^*_\n]+)[*_](?=[^*\w]|$)/g, "$1$2")
    .replace(/^\s{0,3}#{1,6}\s+/gm, "")
    .replace(/^\s*>\s?/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
